import type { JSX } from "react";

import type { IntentName } from "@/design";
import { cn } from "@/shared/lib/cn";

const INTENT_TEXT: Record<IntentName, string> = {
  success: "text-[var(--intent-success-fg)]",
  warning: "text-[var(--intent-warning-fg)]",
  danger: "text-[var(--intent-danger-fg)]",
  info: "text-[var(--intent-info-fg)]",
  neutral: "text-[var(--text-subtle)]",
};

interface LiveDotProps {
  intent?: IntentName;
  /** Pulse ring. Defaults on for `success` (ring expands: alive) and `danger`
   * (ring collapses inward: gone). `.live-ring` is reduced-motion safe. */
  animate?: boolean;
  className?: string;
}

/** A status dot that breathes: tells ops the dashboard is actively polling. */
export function LiveDot({ intent = "success", animate, className }: LiveDotProps): JSX.Element {
  const pulse = animate ?? (intent === "success" || intent === "danger");
  const inward = intent === "danger";
  return (
    <span
      aria-hidden
      className={cn("relative inline-flex h-2 w-2 shrink-0", INTENT_TEXT[intent], className)}
      data-testid="live-dot"
      data-animate={pulse ? (inward ? "in" : "out") : "false"}
    >
      {pulse && (
        <span
          className={cn("live-ring absolute inset-0 rounded-full bg-current", inward && "live-ring--in")}
        />
      )}
      <span className="relative inline-flex h-2 w-2 rounded-full bg-current" />
    </span>
  );
}
